import React, { useState } from 'react';
import {
  CalendarDaysIcon,
  CheckCircleIcon,
  TicketIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import MemberAvatar from './MemberAvatar';
import AttributeLabel from './AttributeLabel';

// Kompakte Buchungs-Karte für den Gästekarten-Kontext (Detail-Modal der
// Gästekartenverwaltung). Bewusst eigenständig gehalten – schlankeres Layout
// als EventCardNew: Datum als Corner-Label statt großer Datumsbox, damit die
// volle Breite für Titel/Trainer/Meta frei bleibt.
interface GuestBookingCardData {
  id: number;
  title: string;
  start: string;
  start_time: string;
  end_time?: string;
  creator_name: string;
  creator: string;
  cotrainer?: string;
  cotrainer_name?: string;
  participantCount: number;
  max_teilnehmer?: number;
  farbe?: string;
  cardName?: string;
  booking_date?: string;
}

interface GuestBookingCardProps {
  event: GuestBookingCardData;
  showCancelButton?: boolean;
  onCancel?: () => void;
  BASE_URL?: string;
  cancellationStatus?: {
    allowed: boolean;
    reason?: string;
  };
}

const getUserImageUrl = (userId: string, BASE_URL: string) => {
  const token = localStorage.getItem('jwt_token');
  return `${BASE_URL}/api/members/user-image-url/${userId}?size=S&token=${token}`;
};

const withAlpha = (color: string, alpha: string) =>
  /^#[0-9a-f]{6}$/i.test(color) ? `${color}${alpha}` : 'rgba(100, 116, 139, 0.1)';

// Nur den Vornamen (erstes Wort) anzeigen.
const firstName = (name?: string) => (name || '').trim().split(' ')[0];

const TrainerAvatar: React.FC<{ userId: string; name: string; BASE_URL: string }> = ({
  userId,
  name,
  BASE_URL,
}) => {
  const [imageError, setImageError] = useState(false);
  const safeName = name || 'Unbekannt';
  const [vorname, ...rest] = safeName.split(' ');
  const nachname = rest.join(' ');
  const imageUrl = userId && BASE_URL ? getUserImageUrl(userId, BASE_URL) : undefined;

  return (
    <MemberAvatar
      member={{ vorname, name: nachname }}
      photoUrl={imageUrl}
      hasImageError={imageError}
      onImageError={() => setImageError(true)}
      size="sm"
      className="ring-2 ring-white dark:ring-gray-800"
    />
  );
};

const GuestBookingCard: React.FC<GuestBookingCardProps> = ({
  event,
  showCancelButton = false,
  onCancel,
  BASE_URL = '',
  cancellationStatus,
}) => {
  const startDate = new Date(`${event.start}T${event.start_time || '00:00'}`);
  const endDate = new Date(`${event.start}T${event.end_time || event.start_time || '00:00'}`);
  const isFutureEvent = endDate >= new Date();
  const accentColor = event.farbe || (isFutureEvent ? '#65a30d' : '#94a3b8');
  const visibleParticipantCount = event.participantCount ?? 0;
  const statusLabel = isFutureEvent ? 'Kommend' : 'Vergangen';

  const day = startDate.toLocaleDateString('de-DE', { day: '2-digit' });
  const month = startDate.toLocaleDateString('de-DE', { month: 'short' }).replace('.', '');
  const year = startDate.toLocaleDateString('de-DE', { year: 'numeric' });
  const weekday = startDate.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '');
  const bookingDate = event.booking_date ? new Date(event.booking_date) : null;

  const canCancel = showCancelButton && (isFutureEvent || cancellationStatus?.allowed);
  const cancelBlocked = cancellationStatus?.allowed === false;

  return (
    <article className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition duration-200 hover:border-gray-300 hover:shadow-md dark:border-gray-700 dark:bg-[#1c2432] dark:hover:border-gray-600">
      <div className="absolute inset-y-0 left-0 w-1" style={{ backgroundColor: accentColor }} />

      <div className="p-3 pl-4 sm:p-3.5 sm:pl-5">
        {/* Kopfzeile: Datum als Corner-Label + Status + Kartentyp */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-[11px] font-bold"
            style={{ backgroundColor: withAlpha(accentColor, '18') }}
          >
            <CalendarDaysIcon className="h-3.5 w-3.5" style={{ color: accentColor }} />
            <span className="uppercase tracking-wide text-gray-500 dark:text-gray-400">{weekday}</span>
            <span className="text-gray-900 dark:text-white">{day}. {month}</span>
            <span className="text-gray-400 dark:text-gray-500">{year}</span>
          </span>

          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${isFutureEvent ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-300'}`}>
            {isFutureEvent ? <CalendarDaysIcon className="h-3 w-3" /> : <CheckCircleIcon className="h-3 w-3" />}
            {statusLabel}
          </span>

          {event.cardName && (
            <span className="inline-flex items-center gap-1 rounded-full bg-warning-50 px-2 py-0.5 text-[10px] font-bold text-warning-700 dark:bg-warning-900/20 dark:text-warning-300">
              <TicketIcon className="h-3 w-3" /> {event.cardName}
            </span>
          )}

          {canCancel && (
            <button
              onClick={cancelBlocked ? undefined : onCancel}
              disabled={cancelBlocked}
              className={`ml-auto inline-flex h-7 items-center justify-center gap-1 rounded-lg px-2.5 text-xs font-bold transition ${cancelBlocked ? 'cursor-not-allowed bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500' : 'bg-error-50 text-error-700 hover:bg-error-100 dark:bg-error-900/20 dark:text-error-300 dark:hover:bg-error-900/30'}`}
              aria-label={cancelBlocked ? 'Stornierung nicht möglich' : 'Buchung stornieren'}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
              Stornieren
            </button>
          )}
        </div>

        {/* Titel + Trainer */}
        <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="text-base font-bold leading-snug text-gray-900 dark:text-white">{event.title || 'Unbenanntes Event'}</h3>
          <span className="text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
          <div className="flex min-w-0 items-center gap-1.5">
            <TrainerAvatar userId={event.creator} name={event.creator_name || 'Unbekannt'} BASE_URL={BASE_URL} />
            <span className="truncate text-xs font-bold text-gray-600 dark:text-gray-300">{firstName(event.creator_name) || 'Trainer'}</span>
          </div>
          {event.cotrainer_name?.trim() && (
            <div className="flex min-w-0 items-center gap-1.5 text-gray-400">
              <span className="text-xs">+</span>
              <TrainerAvatar userId={event.cotrainer || ''} name={event.cotrainer_name} BASE_URL={BASE_URL} />
              <span className="truncate text-xs font-bold text-gray-600 dark:text-gray-300">{firstName(event.cotrainer_name)}</span>
            </div>
          )}
        </div>

        {/* Meta: Zeit + Teilnehmer */}
        <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-gray-600 dark:text-gray-300">
          <AttributeLabel iconKey="time">
            {event.start_time || '00:00'}{event.end_time ? ` – ${event.end_time}` : ''} Uhr
          </AttributeLabel>
          <AttributeLabel iconKey="participants">
            {visibleParticipantCount}{event.max_teilnehmer ? ` / ${event.max_teilnehmer}` : ''} Teilnehmer
          </AttributeLabel>
        </div>

        {bookingDate && (
          <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
            Gebucht am {bookingDate.toLocaleDateString('de-DE')} um {bookingDate.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
          </p>
        )}

        {canCancel && cancelBlocked && cancellationStatus?.reason && (
          <p className="mt-1.5 text-[11px] text-gray-500 dark:text-gray-400">{cancellationStatus.reason}</p>
        )}
      </div>
    </article>
  );
};

export default GuestBookingCard;
